import { useCallback, useEffect, useReducer } from 'react';
import TimelineManager from "../dataset/TimelineManager.js";
import useLazyRef from "./useLazyRef.js";
import { TIMELINE_MANAGER_ACTIONS } from "./Constants.js";
import TimelineUtil from "../TimelineUtil.js";
/**
 * @typedef {{
 *  status: "done|loading|error";
 *  error: Error|null;
 *  nearestData: object;
 * }} TimelineManagerState
 */
const EMPTY_DATA = {
  loading: false,
  error: null,
  nearestData: {}
};
const DEFAULT_PRECISION = {
  minutes: 5
};
function timelineReducer(state, action) {
  const {
    type,
    payload
  } = action;
  const {
    nearestData
  } = state;
  switch (type) {
    case TIMELINE_MANAGER_ACTIONS.DATASET_LOADING_STARTED:
      {
        const currentDataset = nearestData?.[payload.datasetId];
        return {
          ...state,
          loading: true,
          nearestData: {
            ...nearestData,
            [payload.datasetId]: {
              ...currentDataset,
              status: 'loading'
            }
          }
        };
      }
    case TIMELINE_MANAGER_ACTIONS.DATASET_LOADED:
      {
        const currentDataset = nearestData?.[payload.datasetId];
        const nState = {
          ...state,
          nearestData: {
            ...nearestData,
            [payload.datasetId]: {
              ...currentDataset,
              status: 'done',
              data: payload.data
            }
          }
        };
        const loading = Object.values(nState.nearestData).some(datum => {
          return datum.status === 'loading';
        });
        nState.loading = loading;
        return nState;
      }
    case TIMELINE_MANAGER_ACTIONS.DATASET_ADDED:
      {
        return {
          ...state,
          nearestData: {
            ...nearestData,
            [payload.datasetId]: {
              status: null,
              data: null
            }
          }
        };
      }
    case TIMELINE_MANAGER_ACTIONS.ERROR_THROWN:
      {
        const currentDataset = nearestData?.[payload.datasetId];
        const nState = {
          ...state,
          nearestData: {
            ...nearestData,
            [payload.datasetId]: {
              ...currentDataset,
              status: 'error',
              error: payload.error,
              data: null
            }
          }
        };
        const loading = Object.values(nState.nearestData).some(datum => {
          return datum.status === 'loading';
        });
        nState.loading = loading;
        return nState;
      }
    default:
      return state;
  }
}
export default function useTimelineManager(opts) {
  const {
    precision: tickPrecision = DEFAULT_PRECISION,
    addTickCallback,
    removeTickCallback,
    scrubTime
  } = opts || {};
  const precision = TimelineUtil.convertPrecision(tickPrecision);
  /**
   * @type {[TimelineManagerState,function]}
   */
  const [{
    loading,
    error,
    nearestData
  }, dispatch] = useReducer(timelineReducer, EMPTY_DATA);
  const managerRef = useLazyRef(() => {
    return new TimelineManager({
      precision,
      dispatch
    });
  });
  const addDataset = useCallback(async (key, dataset) => {
    managerRef.current.addDataset(key, dataset);
    await managerRef.current.getNearestData(scrubTime);
  }, []);
  const onTick = useCallback(async time => {
    try {
      await managerRef.current.getNearestData(time);
    } catch (err) {
      console.error(err);
    }
  }, []);
  useEffect(() => {
    addTickCallback(onTick);
    return () => {
      removeTickCallback(onTick);
    };
  }, [onTick, addTickCallback, removeTickCallback]);
  return {
    addDataset,
    error,
    loading,
    state: nearestData
  };
}